import { useSceneEvents } from "@/components/common/scene-events-context";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { getMode } from "@/modes";
import { isWorkflowMode } from "@/modes/mode";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { activateTool, ToolName } from "@/store/ui/ui-slice";
import { includes } from "@faro-lotv/foundation";
import { isEqual, isPlainObject } from "es-toolkit";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import {
  applyNavigationHistoryEntry,
  captureNavigationHistoryEntry,
  isNavigationHistoryEntry,
  isSamePosition,
  NavigationHistoryEntry,
} from "./navigation-history-entry";
import { NavigationView } from "./tracked-state";

/**
 * How long the navigation state has to stay unchanged before it is recorded.
 * A single user action often changes it in several steps, e.g. separate mode and
 * element dispatches followed by a mode correcting its active element.
 */
export const NAVIGATION_SETTLE_MS = 300;

/**
 * How often the camera pose is read, as camera moves do not change the store.
 * Shorter than the settle time, so a moving camera does not look settled.
 */
const CAMERA_POLL_MS = NAVIGATION_SETTLE_MS / 3;

/** Key of the entry in the router location state, distinct from `savedView` */
const STATE_KEY = "navigationHistory";

/**
 * Key storing the tool of a guard entry: an extra history entry at the same
 * position, pushed while a tool like the report panel is open, so that going back
 * stays in the viewer and closes the tool, and going forward opens it again.
 *
 * The guard is needed because the browser only fires `popstate` for entries of
 * this page. Without an entry of our own on top, going back from the first viewer
 * entry leaves the viewer, and there is no event to intercept.
 */
const GUARD_KEY = "navigationHistoryGuard";

// Registry of the open tools that take part in the navigation history. Only tools
// that register through `useToolBrowserHistory` do, currently the report and
// snapshot panels (`create-report-panel.tsx`); any other tool is ignored.
//
// It lives outside of Redux because each entry carries the tool's own close
// callback, which does more than `deactivateTool()` (e.g. clearing hidden
// elements), and callbacks do not belong in the store. The listeners and
// `hasOpenTool` make it an external store that `useNavigationHistory` reads with
// `useSyncExternalStore`, so it re-renders whenever such a tool opens or closes.

/** An open tool that closes on the browser's back button */
type OpenTool = {
  /** The open tool */
  tool: ToolName;

  /** Closes the tool */
  close(): void;
};

const openTools = new Set<OpenTool>();
const openToolListeners = new Set<() => void>();

/**
 * @param listener Called when a tool is opened or closed
 * @returns a function removing the listener
 */
function subscribeToOpenTools(listener: () => void): () => void {
  openToolListeners.add(listener);
  return () => openToolListeners.delete(listener);
}

/** @returns whether a tool that closes on the browser's back button is open */
function hasOpenTool(): boolean {
  return openTools.size > 0;
}

/**
 * @param state The router location state
 * @returns the navigation entry stored in it
 */
function readEntry(state: unknown): NavigationHistoryEntry | undefined {
  const entry = isPlainObject(state) ? state[STATE_KEY] : undefined;
  return isNavigationHistoryEntry(entry) ? entry : undefined;
}

/**
 * @param state The router location state
 * @returns the tool of a guard entry, or undefined for any other entry
 */
function readGuardTool(state: unknown): ToolName | undefined {
  const tool = isPlainObject(state) ? state[GUARD_KEY] : undefined;
  return includes(Object.values(ToolName), tool) ? tool : undefined;
}

/**
 * The state is replaced rather than merged on purpose: carrying a share link's
 * `savedView` along would apply it again on every visit of the entry.
 *
 * @param entry The navigation entry to store
 * @param guardTool The open tool, if the history entry is a guard entry
 * @returns the router location state to store
 */
function toState(
  entry: NavigationHistoryEntry | undefined,
  guardTool: ToolName | undefined,
): Record<string, unknown> {
  return guardTool
    ? { [STATE_KEY]: entry, [GUARD_KEY]: guardTool }
    : { [STATE_KEY]: entry };
}

/**
 * @returns the position of the current browser history entry, as tracked by the
 * router, to tell back from forward
 */
function historyIndex(): number {
  return window.history.state?.idx ?? 0;
}

/**
 * @param view The current 3d view
 * @param isEnabled Whether the navigation history is enabled
 * @returns the navigation state to record, updated on store changes and while the
 * camera moves
 */
function useNavigationHistoryEntry(
  view: NavigationView,
  isEnabled: boolean,
): NavigationHistoryEntry | undefined {
  const store = useAppStore();
  const [entry, setEntry] = useState<NavigationHistoryEntry>();
  useEffect(() => {
    if (!isEnabled) return;
    function update(): void {
      const next = captureNavigationHistoryEntry(store.getState(), view);
      setEntry((previous) => (isEqual(previous, next) ? previous : next));
    }
    update();
    const unsubscribe = store.subscribe(update);
    const interval = setInterval(update, CAMERA_POLL_MS);
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [isEnabled, store, view]);
  return entry;
}

/**
 * Records the user's navigation in the browser history, so the browser's back and
 * forward buttons return to a previous position. The url does not change.
 * Inactive while the `NavigationHistory` feature is disabled.
 */
export function useNavigationHistory(): void {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const isEnabled = useAppSelector(
    selectHasFeature(Features.NavigationHistory),
  );
  const { cameras, controls } = useViewRuntimeContext();
  const sceneEvents = useSceneEvents();
  const view = useMemo(
    () => ({ cameras, controls, sceneEvents }),
    [cameras, controls, sceneEvents],
  );
  const entry = useNavigationHistoryEntry(view, isEnabled);

  // Whether a registered tool (report or snapshot panel) is open. It drives the
  // guard entry: pushed on top while such a tool is open, removed once it closes.
  // Recording also updates entries in place meanwhile, so back closes the tool first
  const isToolOpen = useSyncExternalStore(subscribeToOpenTools, hasOpenTool);
  const currentEntry = readEntry(location.state);
  // The tool of the current history entry if it is a guard: going back from it
  // closes that tool, going forward to it opens the tool again
  const guardTool = readGuardTool(location.state);
  const isOnGuard = guardTool !== undefined;

  // Set while a restored entry settles, so the state it settles into updates that
  // entry instead of pushing a new one, which would drop the forward history
  const [isRestoring, setIsRestoring] = useState(false);

  // Set while a guard entry is removed, so going back to the entry below it does
  // not restore anything
  const isRemovingGuard = useRef(false);

  // The viewer state when an entry could not be restored. It is not recorded
  // until its position changes, so the history keeps the entry instead of
  // replacing it, and going back again moves past it
  const keptEntry = useRef<NavigationHistoryEntry>(undefined);

  // Records the navigation state once it settled: pushes an entry per user
  // action, or updates the current one for the first entry, after a restore, while
  // a tool is open, so back still closes it first, and when only the camera moved
  useEffect(() => {
    if (!isEnabled || !entry) return;
    if (!isSamePosition(entry, keptEntry.current)) {
      keptEntry.current = undefined;
    }
    const timeout = setTimeout(() => {
      if (isSamePosition(entry, keptEntry.current)) return;
      if (!isEqual(currentEntry, entry)) {
        navigate(location, {
          state: toState(entry, guardTool),
          replace:
            isRestoring ||
            !currentEntry ||
            isOnGuard ||
            isToolOpen ||
            isSamePosition(currentEntry, entry),
        });
      }
      setIsRestoring(false);
    }, NAVIGATION_SETTLE_MS);
    return () => clearTimeout(timeout);
  }, [
    currentEntry,
    entry,
    guardTool,
    isEnabled,
    isOnGuard,
    isRestoring,
    isToolOpen,
    location,
    navigate,
  ]);

  // Keeps a guard entry on top while a tool is open, and removes it once the
  // tool was closed another way, e.g. by its own button
  useEffect(() => {
    if (!isEnabled || isToolOpen === isOnGuard) return;
    const timeout = setTimeout(() => {
      const openTool = [...openTools].at(0);
      if (openTool) {
        navigate(location, { state: toState(currentEntry, openTool.tool) });
      } else {
        isRemovingGuard.current = true;
        navigate(-1);
      }
    }, NAVIGATION_SETTLE_MS);
    return () => clearTimeout(timeout);
  }, [currentEntry, isEnabled, isOnGuard, isToolOpen, location, navigate]);

  // The location the user was at before the last location change
  const previous = useRef({
    key: location.key,
    guardTool,
    index: historyIndex(),
  });

  // Handles the browser's back and forward buttons: going back from a guard
  // entry closes the open tool, going forward to one opens it again, and any
  // other entry is restored in a viewing mode
  useEffect(() => {
    const left = previous.current;
    if (left.key === location.key) return;
    previous.current = { key: location.key, guardTool, index: historyIndex() };
    if (!isEnabled || navigationType !== "POP") return;

    if (isRemovingGuard.current) {
      isRemovingGuard.current = false;
      return;
    }
    if (left.guardTool && hasOpenTool()) {
      if (historyIndex() < left.index) {
        openTools.forEach(({ close }) => close());
      }
      return;
    }
    if (guardTool && !hasOpenTool() && historyIndex() > left.index) {
      dispatch(activateTool(guardTool));
      return;
    }
    if (isWorkflowMode(getMode(selectModeName(store.getState())))) return;
    if (!currentEntry) return;
    if (dispatch(applyNavigationHistoryEntry(currentEntry, view))) {
      setIsRestoring(true);
    } else {
      keptEntry.current = captureNavigationHistoryEntry(store.getState(), view);
    }
  }, [
    currentEntry,
    dispatch,
    guardTool,
    isEnabled,
    location,
    navigationType,
    store,
    view,
  ]);
}

/**
 * Closes an open tool, like the report panel, when the user presses the browser's
 * back button, instead of navigating back, and opens it again on forward.
 *
 * Calling it while the tool is mounted is what makes the tool take part in the
 * navigation history; it is used by the report and snapshot panels.
 *
 * @param tool The open tool
 * @param close The action closing the tool
 */
export function useToolBrowserHistory(tool: ToolName, close: () => void): void {
  useEffect(() => {
    const openTool = { tool, close };
    openTools.add(openTool);
    openToolListeners.forEach((listener) => listener());
    return () => {
      openTools.delete(openTool);
      openToolListeners.forEach((listener) => listener());
    };
  }, [tool, close]);
}
